/* La pestaña Guion: la ficha de la escena que estás escribiendo, al lado del texto.
 *
 * REGLA DE ESTA PESTAÑA (9-oct-2026, tras colgar el Word de Rais): no hace NADA con Word por su
 * cuenta. Tú eliges la escena en la lista y ella enseña su ficha; lo único que lee del documento es
 * lo que tengas seleccionado, y solo cuando pulsas «Medir». Seguir al cursor volverá cuando se haya
 * probado en Word, no antes. Lo vigila qa/guion-panel-guardias.test.mjs.
 *
 * «Verificar» (10-oct) tampoco le pregunta nada a Word ni escribe en el documento: el motor lee la
 * escena del fichero guardado, la comprueba contra la ficha y aquí se enseña lo que dice.
 */
import { useCallback, useEffect, useState } from "react";
import * as word from "./office";
import * as api from "./guionApi";
import Detector from "./Detector";
import GuionPropuesto from "./GuionPropuesto";
import { rutaLocal, carpetaDe, nombreDe, avance, fraseDeMuletillas, nombresDeMuletillas, miles, horaDe, diaYHora, dolares, cuentaDeCambios, porCiento, ENTRADAS, nombreDeEntrada, fraseDeMedida, fraseDeNovela, fraseDelAviso, fraseDeLoLeido, fraseDeCoincidencia, LEYENDA_DEL_SEMAFORO, LETRA_PEQUENA_DE_LA_IA, NOMBRE_DE_ZONA, type Zona, fraseDeCampos, cuentaDeCampo, marcaDe, pendientesDe,
  tandasQueDecir, fraseDeTandaSinLeer, fraseDeLaBusqueda, fraseDeLeidaEnOtro, leidoDe, ROTULO_LA_DECIDI, AYUDA_DE_LA_TANDA,
  type Autoria, type NovelaEstado, type Construida, type Indice, type SinGuion, type Ficha, type MedidaDeEscena, type Verificada, type Llevada, type Turno, type TandaPorLeer } from "./guionLogica";
import { reportPanelError } from "./companion";

const CY = "var(--hi-humano)";
const GD = "var(--hi-oficio)";
const TENUE = "var(--hi-tenue)";
const RAYA = "1px solid var(--hi-raya)";
const SEMAFORO = { verde: "var(--hi-verde)", ambar: "var(--hi-ambar)", rojo: "var(--hi-rojo)" } as const;
const COLOR_DE = { bien: "var(--hi-bien-suave)", aviso: "var(--hi-aviso)", error: "var(--hi-mal-suave)" } as const;
const COLOR_VEREDICTO = { cumple: SEMAFORO.verde, "a medias": SEMAFORO.ambar, falta: SEMAFORO.rojo, nada: TENUE } as const;
const DICE_VEREDICTO = { cumple: "Cumple.", "a medias": "A medias.", falta: "Falta.", nada: "Sin veredicto." } as const;

/** Lo que la pestaña necesita de Word y del motor. Se puede sustituir para verla sin Word delante. */
export type Puente = Pick<typeof word, "getDocumentUrl">
  & Pick<typeof api, "traerIndice" | "importarGuion" | "fichaDe" | "crearEsqueleto" | "abrirManuscrito" | "medidaDeEscena" | "verificarEscena" | "llevarCambios" | "loUltimoDe" | "discutirConElCoach" | "cerrarEscena" | "reabrirEscena" | "construirNovela" | "empezarAutoria" | "abrirFuente" | "crearGuionEnBlanco" | "estadoDelDetector" | "cerrarLaNovela" | "reabrirLaNovela" | "pasarPorElDetector" | "comprarElDetector" | "dejarDeEsperarElPago" | "leerTandaAqui">;
const DE_VERDAD: Puente = {
  getDocumentUrl: word.getDocumentUrl,
  traerIndice: api.traerIndice, importarGuion: api.importarGuion, fichaDe: api.fichaDe,
  crearEsqueleto: api.crearEsqueleto, abrirManuscrito: api.abrirManuscrito, medidaDeEscena: api.medidaDeEscena,
  verificarEscena: api.verificarEscena, llevarCambios: api.llevarCambios,
  loUltimoDe: api.loUltimoDe, discutirConElCoach: api.discutirConElCoach,
  cerrarEscena: api.cerrarEscena, reabrirEscena: api.reabrirEscena, construirNovela: api.construirNovela, abrirFuente: api.abrirFuente, crearGuionEnBlanco: api.crearGuionEnBlanco,
  empezarAutoria: api.empezarAutoria,
  estadoDelDetector: api.estadoDelDetector, cerrarLaNovela: api.cerrarLaNovela, reabrirLaNovela: api.reabrirLaNovela, pasarPorElDetector: api.pasarPorElDetector,
  comprarElDetector: api.comprarElDetector, dejarDeEsperarElPago: api.dejarDeEsperarElPago,
  leerTandaAqui: api.leerTandaAqui,
};

type Documento = { estado: "leyendo" | "sin_guardar" | "nube" } | { estado: "local"; ruta: string; dir: string };
/** Lo que pasó al llevar los cambios a Word. `existe`: la versión que ya los tiene, o la que es más nueva que esta. */
type Llevar = { estado: "pidiendo" } | { estado: "hecho"; datos: Llevada } | { estado: "error"; texto: string; existe?: string };
type Verificacion = { estado: "pidiendo"; escena: string } | { estado: "hecha"; datos: Verificada } | { estado: "error"; escena: string; texto: string };
/** Lo que pasó al pedir el Word del manuscrito: se dice JUNTO al botón, que es donde está mirando el autor. */
type Empezar = { tono: "bien" | "aviso" | "error"; texto: string; abrir?: string };

// La última escena que miraste en cada documento, para volver a ella al abrir el panel.
const llave = (ruta: string) => `hi.guion.escena:${ruta}`;
const recordada = (ruta: string) => { try { return localStorage.getItem(llave(ruta)); } catch { return null; } };
const recordar = (ruta: string, numero: string) => { try { localStorage.setItem(llave(ruta), numero); } catch { /* sin almacenamiento: no se recuerda */ } };

/**
 * Dos fases, una pestaña cada una, sobre el mismo libro (Rais, 10-oct-2026: «la segunda es hacer el guion; y
 * luego se trabaja el guion, que de hecho es otra cosa»):
 *   · `guion`: el guion como documento —traerlo o crearlo en blanco, cuál se usa, sus escenas, el manuscrito—;
 *   · `escritura`: escena a escena, con su ficha al lado —verificar, llevar a Word, cerrar— y construir la novela.
 * Es un solo componente para que pasar de una a otra no pierda la escena elegida ni vuelva a preguntar nada.
 */
export type VistaDelGuion = "guion" | "escritura";
export default function Guion({ puente = DE_VERDAD, vista = "escritura", alIr }: { puente?: Puente; vista?: VistaDelGuion; alIr?: (p: "biblia" | VistaDelGuion) => void }) {
  const { getDocumentUrl, traerIndice, importarGuion, fichaDe, crearEsqueleto, abrirManuscrito, medidaDeEscena, verificarEscena, llevarCambios, loUltimoDe, discutirConElCoach, cerrarEscena, reabrirEscena, construirNovela, empezarAutoria, abrirFuente, crearGuionEnBlanco, leerTandaAqui } = puente;
  const [docu, setDocu] = useState<Documento>({ estado: "leyendo" });
  const [indice, setIndice] = useState<Indice | SinGuion | null>(null);
  const [ficha, setFicha] = useState<Ficha | null>(null);
  /** Las palabras y las muletillas de IA de la escena elegida, y las de la novela: siempre a la vista. */
  const [medida, setMedida] = useState<MedidaDeEscena | null>(null);
  const [verif, setVerif] = useState<Verificacion | null>(null);
  // Apagado cada vez: que la revisión redacte lo pides tú, escena a escena.
  const [conFrases, setConFrases] = useState(false);
  const [llevado, setLlevado] = useState<Llevar | null>(null);
  // La conversación con el coach sobre la verificación que hay a la vista.
  const [hilo, setHilo] = useState<Turno[]>([]);
  /** Empezar de cero: el guion en blanco. Cuántos capítulos y escenas, y qué pasó al crearlo. */
  const [enBlanco, setEnBlanco] = useState({ titulo: "", capitulos: "30", escenas: "3" });
  const [creado, setCreado] = useState<{ estado: "pidiendo" } | { estado: "hecho"; nombre: string; escenas: number; abierto: boolean } | { estado: "error"; texto: string } | null>(null);
  /** Lo que pasó al pedir que se abra el Word del guion: se dice junto al botón. */
  const [suGuion, setSuGuion] = useState<Empezar | null>(null);
  /** Lo que el coach acaba de retirar: se dice una vez, debajo de su respuesta. */
  const [retirado, setRetirado] = useState<string[]>([]);
  /** Los campos de la ficha con su lista de comprobaciones desplegada. */
  const [desplegados, setDesplegados] = useState<Record<string, boolean>>({});
  const [pregunta, setPregunta] = useState("");
  const [hablando, setHablando] = useState<{ estado: "pidiendo" } | { estado: "error"; texto: string } | null>(null);
  const [cambiada, setCambiada] = useState(false);
  // Las escenas cerradas de la novela, y lo que pasó al cerrar una o al construirla.
  const [novela, setNovela] = useState<NovelaEstado | null>(null);
  const [cierre, setCierre] = useState<{ estado: "pidiendo" } | { estado: "error"; texto: string } | { estado: "igual" } | null>(null);
  // Las tandas de cambios de la escena que siguen sin leer (encargo 0005), y lo que pasó al pulsar «La decidí en otro documento».
  const [porLeer, setPorLeer] = useState<TandaPorLeer[]>([]);
  const [leyendo, setLeyendo] = useState<{ estado: "pidiendo" } | { estado: "hecho"; texto: string } | { estado: "error"; texto: string } | null>(null);
  const [construida, setConstruida] = useState<{ estado: "pidiendo" } | { estado: "hecha"; datos: Construida } | { estado: "error"; texto: string } | null>(null);
  // El registro de autoría del libro: cómo está, con qué entrada se va a empezar y si se ha pedido confirmación.
  const [autoria, setAutoria] = useState<Autoria | null>(null);
  const [entrada, setEntrada] = useState("escaleta");
  const [arranque, setArranque] = useState<{ estado: "confirmar" | "pidiendo" } | { estado: "error"; texto: string } | null>(null);
  // Lo que el autor teclea para la portada. Mientras no toque un campo, vale lo que dice el motor.
  const [portada, setPortada] = useState<{ titulo?: string; autor?: string }>({});
  const [aviso, setAviso] = useState<{ tipo: "error" | "bien"; texto: string } | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [verIndice, setVerIndice] = useState(false);
  const [verEmpezar, setVerEmpezar] = useState(false);
  const [empezar, setEmpezar] = useState<Empezar | null>(null);
  const [verDetalles, setVerDetalles] = useState(false);

  const local = docu.estado === "local" ? docu : null;
  const fallo = (donde: string, e: unknown) => {
    const err = e as { message?: string; codigo?: string };
    setAviso({ tipo: "error", texto: String(err?.message ?? e) });
    if (!err?.codigo) reportPanelError(`guion:${donde}`, e);     // los fallos con nombre ya se explican solos
  };

  /**
   * Las palabras y las muletillas de IA de la escena, y las de la novela. No se selecciona nada ni se le
   * pregunta nada a Word (10-oct, Rais: «en vez de pedirle que seleccione un texto, dar siempre el resultado
   * de la escena»): lo lee el motor del documento GUARDADO. Si un motor anterior no lo sabe, no sale nada.
   */
  const medir = useCallback(async (dir: string, ruta: string, numero: string) => {
    const m = await medidaDeEscena(dir, ruta, numero).catch(() => null);
    if (recordada(ruta) === numero) setMedida(m);        // si ya has pasado a otra escena, esto llega tarde y no se pinta
  }, [medidaDeEscena]);

  const ver = useCallback(async (dir: string, ruta: string, numero: string | null) => {
    if (!numero) return;
    try {
      setFicha(await fichaDe(dir, ruta, numero));
      recordar(ruta, numero);
      setMedida(null);
      void medir(dir, ruta, numero);
      setLlevado(null);
      setVerif(null); setHilo([]); setRetirado([]); setDesplegados({}); setHablando(null); setCambiada(false); setCierre(null);
      setPorLeer([]); setLeyendo(null);
      setVerIndice(false);
      // Lo que ya está pagado no se pierde por cerrar el panel: la última verificación de esta escena, lo
      // hablado de ella y si sus cambios ya fueron a Word se leen de lo guardado junto al libro. No gasta
      // ni pregunta nada a Word. Con un motor anterior, o sin nada guardado, la ficha se queda como estaba.
      const u = await loUltimoDe(dir, ruta, numero).catch(() => null);
      if (u && recordada(ruta) === numero) setPorLeer(u.pendientes ?? []);
      if (u?.verificacion && recordada(ruta) === numero) {      // si ya has pasado a otra escena, esto llega tarde y no se pinta
        setVerif({ estado: "hecha", datos: u.verificacion });
        setHilo(u.hilo ?? []);
        setCambiada(!!u.escena_cambiada);
        if (u.llevada) setLlevado({ estado: "hecho", datos: { ...u.llevada, abierto: false } });
      }
    } catch (e) { fallo("ficha", e); }
  }, [fichaDe, loUltimoDe, medir]);

  /** Lo que hay al abrir: el guion del libro y la última escena que miraste. Sin tocar el documento. */
  const cargar = useCallback(async (dirDelDocumento: string, ruta: string) => {
    try {
      const i = await traerIndice(dirDelDocumento, ruta);
      setIndice(i);
      if (!i.hay) return;
      // La novela construida vive en `<libro>/HumanInk/`: el libro es la carpeta de arriba (21-oct, DEM).
      const dir = i.libro || dirDelDocumento;
      if (i.libro && i.libro !== dirDelDocumento) setDocu({ estado: "local", ruta, dir: i.libro });
      setNovela(i.novela ?? null);
      setAutoria(i.autoria ?? null);
      // Con la novela abierta no se abre ninguna escena: se escriben en el manuscrito, y eso es lo que se dice.
      if (i.novela_abierta) return;
      // Donde estabas: lo último que miraste en ESTE documento o, si es una versión recién creada, la escena
      // en la que ibas en la anterior (10-oct: «te abre otro documento, abres el companion y has perdido todo»).
      const ultima = recordada(ruta) ?? i.escena_reciente ?? null;
      if (ultima) await ver(dir, ruta, ultima);
      else setVerIndice(true);
    } catch (e) { fallo("cargar", e); }
  }, [traerIndice, ver]);

  /** Dónde está guardado el documento. Es lo único que se le pregunta a Word al abrir la pestaña. */
  const mirar = useCallback(async () => {
    const url = await getDocumentUrl();
    const ruta = rutaLocal(url);
    if (!ruta) return setDocu({ estado: url ? "nube" : "sin_guardar" });
    const dir = carpetaDe(ruta);
    setDocu({ estado: "local", ruta, dir });
    await cargar(dir, ruta);
  }, [getDocumentUrl, cargar]);

  useEffect(() => {
    void mirar();
  }, [mirar]);

  // WORD PUEDE TARDAR EN SABER DÓNDE ESTÁ EL DOCUMENTO (10-oct-2026). Al abrirse la versión nueva con el
  // panel ya puesto, Word contestó «sin sitio» a un documento que estaba guardado; la pestaña le dijo al
  // autor que lo guardara, y lo guardó con otro nombre: el manuscrito, bifurcado. Mientras no se sepa el
  // sitio se vuelve a preguntar cada vez que el autor entra en el panel, y hay un botón para pedirlo.
  // Sin temporizadores: es la misma pregunta de siempre, hecha cuando él vuelve.
  const sinSitio = docu.estado === "sin_guardar";
  useEffect(() => {
    if (!sinSitio) return;
    const otraVez = () => { void mirar(); };
    window.addEventListener("focus", otraVez);
    return () => window.removeEventListener("focus", otraVez);
  }, [sinSitio, mirar]);

  // LA MEDIDA DE LA ESCENA SE PONE AL DÍA SOLA al volver al panel (se viene de escribir y guardar en Word).
  // Sin temporizadores y sin preguntarle nada a Word: el motor vuelve a leer el documento guardado.
  const rutaDeAhora = docu.estado === "local" ? docu.ruta : null, dirDeAhora = docu.estado === "local" ? docu.dir : null, escenaDeAhora = ficha?.numero ?? null;
  useEffect(() => {
    if (!rutaDeAhora || !dirDeAhora || !escenaDeAhora) return;
    const alVolver = () => { void medir(dirDeAhora, rutaDeAhora, escenaDeAhora); };
    window.addEventListener("focus", alVolver);
    return () => window.removeEventListener("focus", alVolver);
  }, [rutaDeAhora, dirDeAhora, escenaDeAhora, medir]);

  const traer = async (nombre?: string) => {
    if (!local) return;
    setOcupado("Leyendo el guion…"); setAviso(null);
    try {
      const i = await importarGuion(local.dir, local.ruta, nombre);
      setIndice(i);
      setAviso({ tipo: "bien", texto: `Guion leído: «${i.titulo}», ${i.resumen.capitulos} capítulos y ${i.resumen.escenas} escenas.` });
      if (ficha) await ver(local.dir, local.ruta, ficha.numero);       // la ficha que tenías, con el guion nuevo
      else setVerIndice(true);
    } catch (e) { fallo("importar", e); } finally { setOcupado(null); }
  };

  /**
   * Comprueba la escena contra su ficha, las reglas del guion y la biblia. No lee nada de Word ni
   * escribe en el documento: el motor coge la escena del fichero GUARDADO. Gasta de la clave del autor,
   * así que solo corre cuando él pulsa.
   */
  /**
   * `otraVez`: aunque la escena no haya cambiado desde la última verificación. Sin eso, el motor devuelve la
   * guardada (no se llama al modelo ni se cobra), y con ella vuelve lo que ya se habló y lo que ya se llevó.
   */
  const pedirVerificacion = async (otraVez: boolean) => {
    if (!local || !ficha) return;
    const escena = ficha.numero;
    setVerif({ estado: "pidiendo", escena });
    setLlevado(null); setHilo([]); setRetirado([]); setHablando(null); setCambiada(false);
    try {
      const datos = await verificarEscena(local.dir, local.ruta, escena, conFrases, otraVez);
      setVerif({ estado: "hecha", datos });
      if (datos.ya_verificada) {
        const u = await loUltimoDe(local.dir, local.ruta, escena).catch(() => null);
        if (u?.verificacion?.registro === datos.registro) {
          setHilo(u.hilo ?? []);
          if (u.llevada) setLlevado({ estado: "hecho", datos: { ...u.llevada, abierto: false } });
        }
      }
    } catch (e) {
      const err = e as { message?: string; codigo?: string };
      setVerif({ estado: "error", escena, texto: String(err?.message ?? e) });
      if (!err?.codigo) reportPanelError("guion:verificar", e);
    }
  };
  const verificar = () => pedirVerificacion(false);
  const verificarOtraVez = () => pedirVerificacion(true);

  /**
   * Lleva a Word los cambios de la verificación: el motor crea la versión siguiente del manuscrito con los
   * cambios marcados y la abre. Aquí no se escribe nada en el documento abierto ni se llama a Word.
   */
  const llevar = async () => {
    if (!local || !ficha) return;
    setLlevado({ estado: "pidiendo" });
    try {
      setLlevado({ estado: "hecho", datos: await llevarCambios(local.dir, local.ruta, ficha.numero) });
    } catch (e) {
      const err = e as { message?: string; codigo?: string; datos?: { existe?: string | null } };
      setLlevado({ estado: "error", texto: String(err?.message ?? e), existe: err?.datos?.existe ?? undefined });
      if (!err?.codigo) reportPanelError("guion:llevar", e);
    }
  };

  /** Una pregunta al coach sobre la verificación: gasta de la clave del autor, así que solo al pulsar. */
  const preguntar = async () => {
    const mensaje = pregunta.trim();
    if (!local || !ficha || !mensaje) return;
    setHablando({ estado: "pidiendo" });
    setRetirado([]);
    try {
      const r = await discutirConElCoach(local.dir, local.ruta, ficha.numero, mensaje);
      setHilo((h) => [...h, { de: "autor", texto: mensaje }, { de: "coach", texto: r.respuesta }]);
      // Si el coach ha retirado algo, queda apuntado junto al libro y la verificación llega ya recalculada.
      setRetirado((r.retirados ?? []).map((x) => x.texto));
      if (r.verificada) setVerif({ estado: "hecha", datos: r.verificada });
      setPregunta("");
      setCambiada(!!r.escena_cambiada);
      setHablando(null);
    } catch (e) {
      const err = e as { message?: string; codigo?: string };
      setHablando({ estado: "error", texto: String(err?.message ?? e) });
      if (!err?.codigo) reportPanelError("guion:discutir", e);
    }
  };

  /**
   * Cerrar la escena: dar por bueno el texto que tiene ahora en el documento GUARDADO. Lo lee el motor del
   * fichero; aquí no se pregunta nada a Word. Con las cerradas se construye la novela.
   */
  const cerrar = async () => {
    if (!local || !ficha) return;
    setCierre({ estado: "pidiendo" });
    try {
      const r = await cerrarEscena(local.dir, local.ruta, ficha.numero);
      setNovela((n) => ({ ...(n ?? {}), cerradas: { ...(n?.cerradas ?? {}), [r.escena]: { palabras: r.palabras, cuando: r.cuando, de: r.de, entero: r.entero, coincide: r.coincide ?? null } },
                          total: r.total, hechas: r.hechas, palabras: n?.palabras ?? 0,
                          // Con el cierre llega la medida de la escena y la de la novela, ya recalculadas.
                          medida: r.medida_novela ?? n?.medida }));
      setCierre(r.igual ? { estado: "igual" } : null);
      setConstruida(null);
      // Lo que sigue sin leer después de cerrar: se dice aquí mismo (un motor anterior no lo manda).
      if (r.pendientes) { setPorLeer(r.pendientes); setLeyendo(null); }
    } catch (e) {
      const err = e as { message?: string; codigo?: string };
      setCierre({ estado: "error", texto: String(err?.message ?? e) });
      if (!err?.codigo) reportPanelError("guion:cerrar", e);
    }
  };

  /**
   * «La decidí en otro documento» (encargo 0005): el autor decidió los cambios en ESTE documento, guardado con otro nombre,
   * y la versión entregada sigue con sus marcas. El motor lee de aquí lo que decidió, si este documento puede tenerlo, y si
   * no, dice por qué. Aquí no se le pregunta nada a Word: el motor lee el documento guardado.
   */
  const laDecidiAqui = async () => {
    if (!local || !ficha) return;
    setLeyendo({ estado: "pidiendo" });
    try {
      const r = await leerTandaAqui(local.dir, local.ruta, ficha.numero);
      const leidas = r.tandas.filter((t) => t.estado === "leida");
      setLeyendo({ estado: "hecho", texto: leidas.length ? leidas.map(fraseDeLeidaEnOtro).join(" ") : "No queda ninguna tanda de esta escena que leer de aquí." });
      setPorLeer(tandasQueDecir(r.tandas, ""));
      // Lo llevado a Word de esta escena, ya leído: deja de mandarle a decidir en la versión entregada.
      const suya = leidas.find((t) => llevado?.estado === "hecho" && t.version === llevado.datos.version);
      if (suya) setLlevado((l) => (l?.estado === "hecho" ? { ...l, datos: { ...l.datos, leido: leidoDe(suya) } } : l));
      // La medida, ya con lo leído (como al cerrar).
      if (r.medida_novela) setNovela((n) => (n ? { ...n, medida: r.medida_novela ?? n.medida } : n));
    } catch (e) {
      const err = e as { message?: string; codigo?: string };
      setLeyendo({ estado: "error", texto: String(err?.message ?? e) });
      if (!err?.codigo) reportPanelError("guion:leer-tanda", e);
    }
  };

  const reabrir = async () => {
    if (!local || !ficha) return;
    try {
      const r = await reabrirEscena(local.dir, local.ruta, ficha.numero);
      setNovela((n) => { const cerradas = { ...(n?.cerradas ?? {}) }; delete cerradas[r.escena]; return { cerradas, total: r.total, hechas: r.hechas, palabras: n?.palabras ?? 0 }; });
      setCierre(null); setConstruida(null);
    } catch (e) { setCierre({ estado: "error", texto: String((e as Error)?.message ?? e) }); }
  };

  /**
   * Empezar el registro de autoría. El punto de entrada no se puede cambiar después: se pide dos veces,
   * la primera enseña con qué se va a empezar. Lo guarda el motor junto al libro; aquí no se toca Word.
   */
  const empezarRegistro = async () => {
    if (!local) return;
    if (arranque?.estado !== "confirmar") { setArranque({ estado: "confirmar" }); return; }
    setArranque({ estado: "pidiendo" });
    try {
      setAutoria(await empezarAutoria(local.dir, local.ruta, entrada));
      setArranque(null);
    } catch (e) {
      const err = e as { message?: string; codigo?: string; datos?: { estado?: Autoria } };
      if (err?.datos?.estado) setAutoria(err.datos.estado);
      setArranque({ estado: "error", texto: String(err?.message ?? e) });
      if (!err?.codigo) reportPanelError("guion:autoria", e);
    }
  };

  const tituloDeLaNovela = portada.titulo ?? novela?.datos?.titulo ?? "";
  const autorDeLaNovela = portada.autor ?? novela?.datos?.autor ?? "";
  /** Empezar de cero: el motor crea el Word de un guion en blanco en la carpeta del documento. No se toca Word ni se trae solo. */
  const guionEnBlanco = async () => {
    if (!local) return;
    setCreado({ estado: "pidiendo" });
    try {
      const r = await crearGuionEnBlanco(local.dir, { titulo: enBlanco.titulo.trim(), capitulos: Number(enBlanco.capitulos), escenas: Number(enBlanco.escenas) });
      setCreado({ estado: "hecho", nombre: r.nombre, escenas: r.escenas, abierto: r.abierto });
      setIndice(await traerIndice(local.dir, local.ruta));       // ahora la carpeta tiene un guion que traer
    } catch (e) {
      const err = e as { message?: string; codigo?: string };
      setCreado({ estado: "error", texto: String(err?.message ?? e) });
      if (!err?.codigo) reportPanelError("guion:plantilla", e);
    }
  };

  /** Abrir en Word el guion de este libro. El fichero lo decide el motor: de aquí no sale ningún nombre. No se toca Word. */
  const abrirElGuion = async () => {
    if (!local) return;
    try {
      const r = await abrirFuente(local.dir, local.ruta, "guion");
      setSuGuion(r.abierto
        ? { tono: "bien", texto: `«${r.nombre}» se está abriendo en Word.` }
        : { tono: "aviso", texto: `No he podido abrirlo desde aquí. Está en la carpeta del libro: ${r.nombre}.` });
    } catch (e) {
      const err = e as { message?: string; codigo?: string };
      setSuGuion({ tono: "error", texto: String(err?.message ?? e) });
      if (!err?.codigo) reportPanelError("guion:abrir-guion", e);
    }
  };

  /** Construir la novela: un Word nuevo con la última versión cerrada de cada escena. Lo monta el motor. */
  const construir = async () => {
    if (!local) return;
    setConstruida({ estado: "pidiendo" });
    try {
      const hecha = await construirNovela(local.dir, local.ruta, { titulo: tituloDeLaNovela, autor: autorDeLaNovela });
      setConstruida({ estado: "hecha", datos: hecha });
      // Lo construido es lo guardado: la próxima vez los campos arrancan con ello.
      setNovela((n) => (n ? { ...n, datos: { titulo: hecha.titulo ?? tituloDeLaNovela, autor: hecha.autor ?? autorDeLaNovela } } : n));
      setPortada({});
    } catch (e) {
      const err = e as { message?: string; codigo?: string };
      setConstruida({ estado: "error", texto: String(err?.message ?? e) });
      if (!err?.codigo) reportPanelError("guion:construir", e);
    }
  };

  /** Abre otra versión del manuscrito (la que se acaba de crear, o la que es más nueva que esta). */
  const abrirVersion = async (nombre: string) => {
    if (!local) return;
    try {
      const r = await abrirManuscrito(local.dir, nombre);
      setAviso(r.abierto ? { tipo: "bien", texto: `«${r.nombre}» se está abriendo en Word.` }
                         : { tipo: "error", texto: `No he podido abrirla desde aquí. Está en la carpeta de este documento: ${r.nombre}.` });
    } catch (e) { fallo("abrir", e); }
  };

  const esqueleto = async () => {
    if (!local) return;
    setEmpezar({ tono: "aviso", texto: "Creando el Word del manuscrito…" });
    try {
      const r = await crearEsqueleto(local.dir, local.ruta);
      setEmpezar(r.abierto
        ? { tono: "bien", texto: `Creado «${r.nombre}», con sus ${r.escenas} escenas. Se está abriendo en Word.` }
        : { tono: "bien", texto: `Creado «${r.nombre}», con sus ${r.escenas} escenas, en la carpeta de este documento.`, abrir: r.nombre });
    } catch (e) {
      const err = e as { message?: string; codigo?: string; datos?: { existe?: string | null } };
      // Que ya exista no es un fallo: es el manuscrito del libro, y lo que toca es abrirlo.
      if (err?.codigo === "ya_hay_manuscrito") {
        return setEmpezar({ tono: "aviso", texto: String(err.message), abrir: err.datos?.existe ?? undefined });
      }
      setEmpezar({ tono: "error", texto: String(err?.message ?? e) });
      if (!err?.codigo) reportPanelError("guion:esqueleto", e);
    }
  };

  const abrir = async (nombre: string) => {
    if (!local) return;
    try {
      const r = await abrirManuscrito(local.dir, nombre);
      setEmpezar(r.abierto
        ? { tono: "bien", texto: `«${r.nombre}» se está abriendo en Word.` }
        : { tono: "aviso", texto: `No he podido abrirlo desde aquí. Está en la carpeta de este documento: ${r.nombre}.` });
    } catch (e) { setEmpezar({ tono: "error", texto: String((e as Error)?.message ?? e) }); }
  };

  if (docu.estado === "leyendo") return <p style={nota}>Mirando el documento…</p>;
  if (docu.estado === "sin_guardar") return (
    <div>
      <p style={nota}>Word todavía no me dice dónde está guardado este documento. Si ya está guardado, pulsa aquí: al abrirlo a veces tarda un momento.</p>
      <button onClick={mirar} style={boton}>Volver a mirar</button>
      <p style={nota}>Si es un documento nuevo, guárdalo en la carpeta de tu libro y pulsa el botón. Si es tu manuscrito, no lo guardes con otro nombre: seguirías escribiendo en una copia.</p>
    </div>
  );
  if (docu.estado === "nube") return <p style={nota}>Este documento está en la nube (OneDrive o SharePoint). El guion se lee de una carpeta de este ordenador: guarda una copia en la carpeta de tu libro.</p>;

  // La barra es de la escena: la entera si se ha encontrado en lo guardado, y si no, lo seleccionado.
  const suMedidaDeAhora = medida && ficha && medida.escena === ficha.numero ? medida : null;
  /** El documento abierto es la biblia, el guion…: no es el manuscrito, y no se mide, verifica ni cierra como una escena. */
  const dePartida = !!suMedidaDeAhora?.de_partida;
  /** Lo que pasó al pedir el manuscrito, junto al botón que lo pidió. */
  const avisoDeEmpezar = empezar && (
    <div style={{ marginTop: 8, fontSize: 12, color: COLOR_DE[empezar.tono] }}>
      {empezar.texto}
      {empezar.abrir && <button onClick={() => abrir(empezar.abrir!)} style={{ ...enlace, display: "block", marginTop: 4 }}>Abrir «{empezar.abrir}» en Word</button>}
    </div>
  );
  const escritas = suMedidaDeAhora?.palabras ?? 0;
  const pct = ficha && suMedidaDeAhora?.en_el_documento ? avance(escritas, ficha.palabras) : null;
  // Lo verificado es de UNA escena: si has pasado a otra ficha, no se enseña encima.
  const deEsta = verif && ficha && (verif.estado === "hecha" ? verif.datos.escena : verif.escena) === ficha.numero ? verif : null;
  const hecha = deEsta?.estado === "hecha" ? deEsta.datos : null;
  const cambios = hecha?.cambios ?? [];
  const peso = hecha?.cambios_resumen ?? null;
  const paraLlevar = !!hecha && (cambios.length > 0 || hecha.indicaciones.length > 0);
  const suCierre = ficha ? novela?.cerradas[ficha.numero] ?? null : null;
  const suMedida = ficha && suCierre ? novela?.medida?.escenas?.[ficha.numero] ?? null : null;
  // Las tandas de esta escena sin leer que NO están en el documento abierto (encargo 0005): se dicen junto a cerrar,
  // antes y después de cerrarla, con el botón para decir dónde se decidieron y su respuesta debajo.
  const aDecir = local ? tandasQueDecir(porLeer, nombreDe(local.ruta)) : [];
  const avisoDeTandas = (aDecir.length > 0 || leyendo) && (
    <div style={{ marginTop: 8, fontSize: 12, lineHeight: 1.45, overflowWrap: "anywhere" }}>
      {aDecir.map((t) => (
        <div key={`${t.escena}-${t.verificacion}`} style={{ color: COLOR_DE.aviso, marginBottom: 4 }}>
          {fraseDeTandaSinLeer(t)}
          {fraseDeLaBusqueda(t.buscada) && <div style={{ color: TENUE }}>{fraseDeLaBusqueda(t.buscada)}</div>}
        </div>
      ))}
      {aDecir.some((t) => t.estado !== "sin_fichero") && (
        <>
          <div style={{ fontSize: 11, color: TENUE }}>{AYUDA_DE_LA_TANDA}</div>
          <button onClick={laDecidiAqui} disabled={leyendo?.estado === "pidiendo"} style={{ ...boton, background: "transparent", color: CY, border: `1px solid ${CY}`, marginTop: 6 }}>{ROTULO_LA_DECIDI}</button>
        </>
      )}
      {leyendo?.estado === "pidiendo" && <div style={{ color: COLOR_DE.aviso, marginTop: 4 }}>Leyendo lo que decidiste…</div>}
      {leyendo?.estado === "hecho" && <div style={{ color: COLOR_DE.bien, marginTop: 4 }}>{leyendo.texto}</div>}
      {leyendo?.estado === "error" && <div style={{ color: COLOR_DE.error, marginTop: 4 }}>{leyendo.texto}</div>}
    </div>
  );
  // Solo si el motor lo dice: uno anterior no manda nada de la biblia, y entonces no se afirma que falte.
  const sinBiblia = !!indice?.biblia && !indice.biblia.hay;
  return (
    <div>
      {/* Pegado arriba aunque se haya bajado por la ficha: un aviso que no se ve no ha avisado. */}
      {aviso && <div style={{ ...caja, position: "sticky", top: 0, zIndex: 1, background: "var(--hi-fondo)", borderColor: aviso.tipo === "error" ? "var(--hi-mal-raya)" : "var(--hi-bien-raya)", color: aviso.tipo === "error" ? "var(--hi-mal-suave)" : "var(--hi-bien-suave)" }}>{aviso.texto}</div>}
      {ocupado && <p style={nota}>{ocupado}</p>}
      {indice?.hay && indice.version_mas_nueva && (
        <div style={{ ...caja, borderColor: "var(--hi-aviso-raya)", color: COLOR_DE.aviso }}>
          Estás en una versión anterior del manuscrito. La última es «{indice.version_mas_nueva}»: lo que escribas aquí no estará en ella.
          <button onClick={() => abrirVersion(indice.version_mas_nueva!)} style={{ ...enlace, display: "block", marginTop: 4 }}>Abrir «{indice.version_mas_nueva}» en Word</button>
        </div>
      )}

      {sinBiblia && vista === "guion" && (
        <div style={{ ...caja, borderColor: "var(--hi-aviso-raya)" }}>
          <b>Antes del guion va la biblia.</b>
          <p style={nota}>{indice?.hay
            ? "Este libro tiene guion, pero no encuentro su biblia en la carpeta: las escenas se verifican sin ella."
            : "No se puede hacer el guion del escritor sin una biblia: de ella salen las escenas, y contra ella se comprueban. Tráela, o hazla con tu coach, y vuelve aquí."}</p>
          <button onClick={() => alIr?.("biblia")} style={boton}>Ir a Biblia</button>
        </div>
      )}
      {indice && !indice.hay && vista === "escritura" && (
        <div style={caja}>
          <b>Para escribir con la ficha al lado hace falta el guion.</b>
          <p style={nota}>El orden es este: primero la biblia; de ella sale el guion; y con el guion se escriben las escenas, una a una.</p>
          <button onClick={() => alIr?.(sinBiblia ? "biblia" : "guion")} style={boton}>{sinBiblia ? "Empezar por la biblia" : "Ir a Guion"}</button>
        </div>
      )}
      {indice && !indice.hay && vista === "guion" && !sinBiblia && (
        <div style={caja}>
          <b>Este libro todavía no tiene guion.</b>
          {indice.candidatos.length === 1 ? (
            <>
              <p style={nota}>En la carpeta de este documento he encontrado «{indice.candidatos[0]}». Si es el guion de este libro, tráelo.</p>
              <button onClick={() => traer()} disabled={!!ocupado} style={boton}>Traer ese guion</button>
            </>
          ) : indice.candidatos.length > 1 ? (
            <>
              {/* Con varios, se elige: traer el más reciente sin decirlo trajo un guion en blanco en vez del propuesto (21-oct). */}
              <p style={nota}>En la carpeta hay {indice.candidatos.length} guiones. Trae el de este libro:</p>
              {indice.candidatos.slice(0, 5).map((n) => <EligeGuion key={n} nombre={n} rotulo="Traer este" ocupado={!!ocupado} alElegir={() => traer(n)} />)}
            </>
          ) : (
            <p style={nota}>Si ya lo tienes escrito, pon su Word en la misma carpeta que este documento, con «guion» en el nombre, y vuelve a esta pestaña.</p>
          )}
          {/* Desde la biblia (21-oct-2026): el coach propone el guion y deja un Word nuevo. Al acabar, se mira otra vez la carpeta. */}
          {local && <GuionPropuesto dir={local.dir} alTerminar={async () => { try { setIndice(await traerIndice(local.dir, local.ruta)); } catch { /* se verá al volver */ } }} />}
          <div style={{ borderTop: RAYA, marginTop: 10, paddingTop: 10 }}>
            <div style={rotulo}>Empezar de cero</div>
            <p style={{ ...nota, marginTop: 0 }}>
              Te creo en esta carpeta un Word con el guion en blanco: las secciones generales y una ficha vacía por escena, para que lo rellenes tú (solo, o hablándolo con tu coach en la conversación). Cuando lo tengas, vuelve aquí y tráelo.
            </p>
            <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>Título de la novela
              <input value={enBlanco.titulo} onChange={(e) => setEnBlanco({ ...enBlanco, titulo: e.target.value })} placeholder="Como la carpeta, si lo dejas vacío" style={campo} />
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <label style={{ flex: 1, fontSize: 12 }}>Capítulos
                <input value={enBlanco.capitulos} onChange={(e) => setEnBlanco({ ...enBlanco, capitulos: e.target.value.replace(/\D/g, "").slice(0, 2) })} inputMode="numeric" style={campo} />
              </label>
              <label style={{ flex: 1, fontSize: 12 }}>Escenas por capítulo
                <input value={enBlanco.escenas} onChange={(e) => setEnBlanco({ ...enBlanco, escenas: e.target.value.replace(/\D/g, "").slice(0, 1) })} inputMode="numeric" style={campo} />
              </label>
            </div>
            <button onClick={guionEnBlanco} disabled={!local || creado?.estado === "pidiendo"} style={{ ...boton, marginTop: 8 }}>Crear un guion en blanco</button>
            {creado?.estado === "pidiendo" && <div style={{ fontSize: 12, color: COLOR_DE.aviso, marginTop: 6 }}>Creando el Word…</div>}
            {creado?.estado === "hecho" && (
              <div style={{ fontSize: 12, color: COLOR_DE.bien, marginTop: 6 }}>
                Creado «{creado.nombre}», con {creado.escenas} fichas en blanco{creado.abierto ? ", y abierto en Word" : ": ábrelo desde la carpeta del libro"}. Rellénalo, guárdalo y pulsa aquí arriba «Traer ese guion».
              </div>
            )}
            {creado?.estado === "error" && <div style={{ fontSize: 12, color: COLOR_DE.error, marginTop: 6 }}>{creado.texto}</div>}
          </div>
        </div>
      )}

      {indice?.hay && (
        <>
          {vista === "escritura" ? (
            <div style={{ fontSize: 11, color: TENUE, marginBottom: 8 }}>
              Guion de «{indice.titulo}» · {indice.resumen.escenas} escenas · {miles(indice.resumen.palabras)} palabras previstas
            </div>
          ) : (
            <div style={caja}>
              <div style={{ fontSize: 15, fontWeight: 800 }}>Guion de «{indice.titulo}»</div>
              <div style={{ fontSize: 12, color: TENUE, marginTop: 2 }}>
                {indice.resumen.capitulos} {indice.resumen.capitulos === 1 ? "capítulo" : "capítulos"} · {indice.resumen.escenas} {indice.resumen.escenas === 1 ? "escena" : "escenas"} · {miles(indice.resumen.palabras)} palabras previstas
              </div>
              {indice.fuente && <div style={{ fontSize: 12, marginTop: 6, overflowWrap: "anywhere" }}>Es «{indice.fuente}»{diaYHora(indice.importado) ? `, traído el ${diaYHora(indice.importado)}` : ""}.</div>}
              <button onClick={abrirElGuion} disabled={!local} style={{ ...enlace, display: "block", marginTop: 6 }}>Abrir el guion en Word</button>
              {suGuion && <div style={{ fontSize: 12, color: COLOR_DE[suGuion.tono], marginTop: 4, overflowWrap: "anywhere" }}>{suGuion.texto}</div>}
              {indice.manuscrito === null ? (
                <div style={{ marginTop: 10 }}>
                  {/* Antes de escribir hace falta el manuscrito, y estaba escondido en un desplegable (21-oct). */}
                  <b style={{ fontSize: 13 }}>Siguiente paso: el manuscrito.</b>
                  <p style={nota}>Un Word nuevo con un título por capítulo y otro por escena, donde escribirás cada una. Se crea en la carpeta del libro.</p>
                  <button onClick={esqueleto} disabled={!!ocupado} style={boton}>Crear el manuscrito</button>
                  {avisoDeEmpezar}
                </div>
              ) : (
                <button onClick={() => alIr?.("escritura")} style={{ ...boton, marginTop: 10 }}>Ir a escribir</button>
              )}
              {!!indice.otros_guiones?.length && (
                <div style={{ marginTop: 10, borderTop: RAYA, paddingTop: 8 }}>
                  <div style={{ fontSize: 12, color: TENUE }}>Hay otros guiones en la carpeta. Si el de este libro es otro, tráelo en su lugar: cambia las fichas de todas las escenas.</div>
                  {indice.otros_guiones.map((n) => <EligeGuion key={n} nombre={n} rotulo="Traer este en su lugar" ocupado={!!ocupado} alElegir={() => traer(n)} />)}
                </div>
              )}
            </div>
          )}
          {indice.desfasado && (
            <div style={{ ...caja, borderColor: "var(--hi-aviso-raya)" }}>
              {indice.mas_reciente ? `Hay un guion más reciente en la carpeta: ${indice.mas_reciente}.` : "Has cambiado el guion en Word."}
              <button onClick={() => traer()} disabled={!!ocupado} style={{ ...enlace, display: "block", marginTop: 4 }}>Actualizar el guion</button>
            </div>
          )}

          {vista === "escritura" && indice.novela_abierta && (
            <div style={{ ...caja, borderColor: "var(--hi-aviso-raya)" }}>
              <b>Tienes abierta la novela construida, «{indice.novela_abierta}».</b>
              <p style={nota}>Es el libro montado con tus escenas cerradas. Las escenas se escriben, se verifican y se cierran en el manuscrito.</p>
              {indice.manuscrito
                ? <button onClick={() => abrir(indice.manuscrito!)} style={boton}>Abrir «{indice.manuscrito}» en Word</button>
                : <button onClick={esqueleto} disabled={!!ocupado} style={boton}>Crear el manuscrito</button>}
              {avisoDeEmpezar}
            </div>
          )}
          {vista === "escritura" && (ficha ? (
            <div style={caja}>
              <div style={{ fontSize: 11, color: TENUE }}>
                {ficha.capitulo.titulo} · escena {ficha.capitulo.orden} de {ficha.capitulo.escenas}
                {ficha.capitulo.acto ? ` · ${ficha.capitulo.acto}` : ""}
              </div>
              <div style={{ fontSize: 16, fontWeight: 800, margin: "4px 0 2px" }}>{ficha.titulo}</div>
              {ficha.cabecera && <div style={{ fontSize: 12, color: TENUE }}>{ficha.cabecera}</div>}

              {/* SIEMPRE A LA VISTA: las palabras y las muletillas de IA de la escena (del documento guardado) y las de la
                  novela entera. Nada que seleccionar ni que pulsar: se actualiza al elegir escena y al volver al panel. */}
              {dePartida && (
                <div style={{ ...caja, borderColor: "var(--hi-aviso-raya)", margin: "10px 0 2px" }}>
                  <b>Tienes abierto «{nombreDe(local?.ruta ?? "")}», que no es el manuscrito.</b>
                  <p style={nota}>{indice.novela_abierta ? "Es la novela construida." : "Es uno de tus documentos de partida (la biblia, el guion, la escaleta o el estilo)."} Las escenas se escriben en el manuscrito, que lleva un título por escena («{ficha.numero} · …»): ahí se miden, se verifican y se cierran.</p>
                  {indice.manuscrito
                    ? <button onClick={() => abrir(indice.manuscrito!)} style={boton}>Abrir «{indice.manuscrito}» en Word</button>
                    : <button onClick={esqueleto} disabled={!!ocupado} style={boton}>Crear el manuscrito</button>}
                  {avisoDeEmpezar}
                </div>
              )}
              {suMedidaDeAhora && !dePartida && (
                <div style={{ margin: "10px 0 2px" }}>
                  {suMedidaDeAhora.en_el_documento ? (
                    <>
                      <div style={{ fontSize: 12 }}>
                        <b>{miles(escritas)}</b>{ficha.palabras ? ` de ${miles(ficha.palabras)} palabras previstas` : " palabras"}
                      </div>
                      {pct != null && (
                        <div style={{ height: 4, background: "var(--hi-raya)", borderRadius: 2, marginTop: 4 }}>
                          <div style={{ height: 4, width: `${pct}%`, background: CY, borderRadius: 2 }} />
                        </div>
                      )}
                      <div style={{ fontSize: 12, marginTop: 8 }}>
                        Muletillas de IA en esta escena: <b>{fraseDeMuletillas(suMedidaDeAhora.muletillas)}</b>
                        {suMedidaDeAhora.muletillas.hay && suMedidaDeAhora.muletillas.n > 0 && (
                          <div style={{ color: TENUE }}>{nombresDeMuletillas(suMedidaDeAhora.muletillas.mas_repetidas)}</div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div style={{ fontSize: 12, color: TENUE }}>
                      {suMedidaDeAhora.leido ? "Esta escena no tiene texto en el documento que tienes abierto." : "No he podido leer el documento guardado."}
                    </div>
                  )}
                  <div style={{ fontSize: 12, marginTop: 4 }}>
                    En la novela entera: <b>{fraseDeMuletillas(suMedidaDeAhora.novela)}</b>
                    {suMedidaDeAhora.novela.hay && <span style={{ color: TENUE }}> ({suMedidaDeAhora.novela.escenas} {suMedidaDeAhora.novela.escenas === 1 ? "escena" : "escenas"}, {miles(suMedidaDeAhora.novela.palabras)} palabras)</span>}
                  </div>
                  <div style={{ fontSize: 11, color: TENUE, marginTop: 3 }}>
                    Son fórmulas típicas de la prosa de máquina («cabe señalar que», «en este sentido»…): conviene mirarlas, pero no dicen de quién es el texto. {LETRA_PEQUENA_DE_LA_IA}
                    {" "}Es del texto guardado{horaDe(suMedidaDeAhora.guardado) ? ` a las ${horaDe(suMedidaDeAhora.guardado)}` : ""}: guarda el documento y se pone al día al volver aquí.{" "}
                    <button onClick={() => local && void medir(local.dir, local.ruta, ficha.numero)} style={{ ...enlace, fontSize: 11 }}>Actualizar</button>
                  </div>
                </div>
              )}

              {!dePartida && (<>
              <div style={{ margin: "10px 0 2px", borderTop: RAYA, paddingTop: 8 }}>
                <button onClick={verificar} disabled={deEsta?.estado === "pidiendo"} style={boton}>Verificar esta escena con su ficha</button>
                <label style={{ display: "block", fontSize: 12, marginTop: 6, cursor: "pointer" }}>
                  <input type="checkbox" checked={conFrases} onChange={(e) => setConFrases(e.target.checked)} style={{ marginRight: 6 }} />
                  Proponer también frases cortas
                  <span style={{ display: "block", fontSize: 11, color: TENUE, marginLeft: 20 }}>15 palabras como mucho cada una, nunca dos seguidas. Irán a Word como cambios marcados, con su propia firma, y se cuentan.</span>
                </label>
                {!hecha && deEsta?.estado !== "pidiendo" && (
                  <div style={{ fontSize: 11, color: TENUE, marginTop: 4 }}>
                    Lee la escena tal como está guardada (guarda antes) y la comprueba con la ficha, las reglas del guion y la biblia. No toca tu texto ni te propone frases. Usa tu clave de OpenRouter.
                  </div>
                )}
                {deEsta?.estado === "pidiendo" && <div style={{ fontSize: 12, color: COLOR_DE.aviso, marginTop: 6 }}>Verificando la escena {ficha.numero}… Puede tardar un minuto.</div>}
                {deEsta?.estado === "error" && <div style={{ fontSize: 12, color: COLOR_DE.error, marginTop: 6 }}>{deEsta.texto}</div>}
                {hecha && (
                  <div style={{ marginTop: 8 }}>
                    {hecha.ya_verificada && (
                      <div style={{ fontSize: 12, color: COLOR_DE.aviso, lineHeight: 1.45, marginBottom: 6 }}>
                        Es la verificación del {diaYHora(hecha.ya_verificada)}: la escena no ha cambiado desde entonces, así que no se ha pedido otra ni se ha gastado nada.
                        <button onClick={verificarOtraVez} disabled={deEsta?.estado === "pidiendo"} style={{ ...enlace, display: "block", marginTop: 2 }}>Verificar otra vez de todos modos</button>
                        <span style={{ fontSize: 11, color: TENUE }}>Otra verificación del mismo texto puede dar algún veredicto distinto, y gasta de tu clave.</span>
                      </div>
                    )}
                    {hecha.con_lista && fraseDeCampos(hecha.campos_resumen)
                      ? <>
                          <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.4 }}>{fraseDeCampos(hecha.campos_resumen)}</div>
                          {hecha.resumen && <div style={{ fontSize: 12, lineHeight: 1.4, marginTop: 2, ...(hecha.acordado_despues ? retiradoPorElCoach : {}) }}>
                            {hecha.acordado_despues && "Se dijo antes de lo que acordaste con el coach: "}{hecha.resumen}
                          </div>}
                        </>
                      : <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.4 }}>{hecha.resumen || "Verificada."}</div>}
                    <div style={{ fontSize: 11, color: TENUE, marginTop: 2 }}>
                      La escena {hecha.escena} tal como estaba guardada{horaDe(hecha.guardado) ? ` a las ${horaDe(hecha.guardado)}` : ""}: {miles(hecha.palabras)} palabras. El veredicto de cada campo está abajo, en la ficha.
                    </div>
                    {hecha.indicaciones.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <div style={rotulo}>Lo más importante</div>
                        {hecha.indicaciones.map((x, i) => (
                          <div key={i} style={{ fontSize: 12, lineHeight: 1.45, marginBottom: 6, ...(x.acordado ? retiradoPorElCoach : {}) }}>
                            {x.acordado && <div style={{ fontSize: 11 }}>Retirado al hablarlo con el coach:</div>}
                            <b>{i + 1}. {x.campo || "Escena"}.</b> {x.falta}
                            {x.datos && <div style={{ color: TENUE }}>Datos: {x.datos}</div>}
                            {x.donde && <div style={cita}>Junto a: «{x.donde}»</div>}
                          </div>
                        ))}
                        {hecha.mas_indicaciones > 0 && <div style={{ fontSize: 11, color: TENUE }}>Había {hecha.mas_indicaciones} más: se enseñan las cinco primeras.</div>}
                      </div>
                    )}
                    {hecha.reglas.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <div style={rotulo}>Reglas del encargo</div>
                        {hecha.reglas.map((x, i) => (
                          <div key={i} style={{ fontSize: 12, lineHeight: 1.45, marginBottom: 6, ...(x.acordado ? retiradoPorElCoach : {}) }}>
                            {x.acordado && <div style={{ fontSize: 11 }}>Retirado al hablarlo con el coach:</div>}
                            <b>{x.regla}</b>{x.porque ? ` ${x.porque}` : ""}
                            {x.cita && <div style={cita}>«{x.cita}»</div>}
                          </div>
                        ))}
                      </div>
                    )}
                    {hecha.datos.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <div style={rotulo}>Datos que no cuadran</div>
                        {hecha.datos.map((x, i) => (
                          <div key={i} style={{ fontSize: 12, lineHeight: 1.45, marginBottom: 6, ...(x.acordado ? retiradoPorElCoach : {}) }}>
                            {x.acordado && <div style={{ fontSize: 11 }}>Retirado al hablarlo con el coach:</div>}
                            Según {x.segun === "guion" ? "el guion" : "la biblia"}: <b>{x.dato}</b>{x.porque ? ` ${x.porque}` : ""}
                            {x.en_el_texto && <div style={cita}>En tu texto: «{x.en_el_texto}»</div>}
                          </div>
                        ))}
                      </div>
                    )}
                    {paraLlevar && (
                      <div style={{ marginTop: 10, borderTop: RAYA, paddingTop: 8 }}>
                        <div style={rotulo}>Para llevar a Word</div>
                        {cambios.length > 0 && peso ? (
                          <>
                            <div style={{ fontSize: 12, fontWeight: 700 }}>{cuentaDeCambios(peso)}</div>
                            {cambios.map((c) => (
                              <div key={c.id} style={{ fontSize: 12, lineHeight: 1.45, marginTop: 4 }}>
                                {c.tipo === "proponer"
                                  ? <>Una frase de {c.palabras_no_tuyas} palabras tras <span style={cita}>«{c.original}»</span>. La verás en Word.</>
                                  : <><span style={cita}>«{c.original}»</span> → {c.nuevo ? <span style={cita}>«{c.nuevo}»</span> : "se quita"}</>}
                              </div>
                            ))}
                            <div style={{ fontSize: 12, marginTop: 8 }}>
                              <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 5, marginRight: 6, background: SEMAFORO[peso.zona] }} />
                              {peso.palabras_no_tuyas > 0
                                ? <>Si lo aceptas todo, <b>{miles(peso.palabras_no_tuyas)}</b> de las {miles(peso.palabras_finales)} palabras de la escena no serían tuyas ({porCiento(peso.pct)}).</>
                                : "Nada de esto añade palabras que no sean tuyas."}
                            </div>
                          </>
                        ) : (
                          <div style={{ fontSize: 12 }}>No propone cambios en el texto. Las indicaciones pueden ir a Word como comentarios, cada una en su sitio.</div>
                        )}
                        {!!peso?.vueltos_indicacion && (
                          <div style={{ fontSize: 11, color: TENUE, marginTop: 4 }}>
                            {peso.vueltos_indicacion === 1 ? "Un cambio añadía" : `${peso.vueltos_indicacion} cambios añadían`} texto que no puede entrar{hecha.con_frases ? "" : " sin que lo pidas"}: {peso.vueltos_indicacion === 1 ? "se ha quedado" : "se han quedado"} como indicación, sin el texto.
                          </div>
                        )}
                        {llevado?.estado !== "hecho" && (
                          <>
                            <button onClick={llevar} disabled={llevado?.estado === "pidiendo"} style={{ ...boton, marginTop: 8 }}>Llevar a Word como cambios marcados</button>
                            <div style={{ fontSize: 11, color: TENUE, marginTop: 4 }}>
                              Crea la versión siguiente del manuscrito con los cambios marcados y las indicaciones como comentarios, y la abre. Este documento no se toca. Parte de lo guardado{horaDe(hecha.guardado) ? ` a las ${horaDe(hecha.guardado)}` : ""}.
                            </div>
                          </>
                        )}
                        {llevado?.estado === "pidiendo" && <div style={{ fontSize: 12, color: COLOR_DE.aviso, marginTop: 6 }}>Creando la versión con los cambios…</div>}
                        {llevado?.estado === "error" && (
                          <div style={{ fontSize: 12, color: COLOR_DE.error, marginTop: 6 }}>
                            {llevado.texto}
                            {llevado.existe && <button onClick={() => abrirVersion(llevado.existe!)} style={{ ...enlace, display: "block", marginTop: 4 }}>Abrir «{llevado.existe}» en Word</button>}
                          </div>
                        )}
                        {llevado?.estado === "hecho" && local && llevado.datos.version === nombreDe(local.ruta) && (
                          <div style={{ fontSize: 12, color: COLOR_DE.bien, marginTop: 6, lineHeight: 1.45 }}>
                            Estás en la versión que lleva estos cambios: {llevado.datos.cambios} {llevado.datos.cambios === 1 ? "marcado" : "marcados"} y {llevado.datos.comentarios} {llevado.datos.comentarios === 1 ? "comentario" : "comentarios"}.
                            <div style={{ color: "var(--hi-suave)", marginTop: 4 }}>
                              {llevado.datos.leido
                                ? <>Leído lo que hiciste con ellos: {fraseDeLoLeido(llevado.datos.leido)}{llevado.datos.leido.de ? ` (en «${llevado.datos.leido.de}»)` : ""}.</>
                                : <>Decide con Revisar → Aceptar o Rechazar. Cuando termines, guarda; si quieres un veredicto del texto como quede, verifica otra vez. Lo que decidas se lee al cerrar la escena.</>}
                            </div>
                          </div>
                        )}
                        {llevado?.estado === "hecho" && !(local && llevado.datos.version === nombreDe(local.ruta)) && (
                          <div style={{ fontSize: 12, color: COLOR_DE.bien, marginTop: 6, lineHeight: 1.45 }}>
                            Creada «{llevado.datos.version}»: {llevado.datos.cambios} {llevado.datos.cambios === 1 ? "cambio marcado" : "cambios marcados"} y {llevado.datos.comentarios} {llevado.datos.comentarios === 1 ? "comentario" : "comentarios"}.
                            {llevado.datos.abierto ? " Se está abriendo en Word." : " Está en la carpeta de este documento."}
                            <div style={{ color: "var(--hi-suave)", marginTop: 4 }}>
                              {/* Ya leído (encargo 0005: puede que lo decidiera en un Word guardado con otro nombre): no se le manda a decidir ahí. */}
                              {llevado.datos.leido
                                ? <>Leído lo que hiciste con ellos: {fraseDeLoLeido(llevado.datos.leido)}{llevado.datos.leido.de ? ` (en «${llevado.datos.leido.de}»)` : ""}.</>
                                : <>Decide ahí con Revisar → Aceptar o Rechazar, y sigue escribiendo en esa versión: esta ya la puedes cerrar.
                                  {llevado.datos.firmas.length > 1 && " Las frases propuestas van firmadas aparte («HumanInk · texto propuesto»), por si quieres rechazarlas todas de una vez."}</>}
                            </div>
                            {llevado.datos.sin_comentarios && <div style={{ color: COLOR_DE.aviso }}>Los comentarios no han podido entrar; las indicaciones las tienes aquí arriba.</div>}
                            {!llevado.datos.abierto && <button onClick={() => abrirVersion(llevado.datos.version)} style={{ ...enlace, display: "block", marginTop: 4 }}>Abrir «{llevado.datos.version}» en Word</button>}
                          </div>
                        )}
                      </div>
                    )}
                    <div style={{ marginTop: 10, borderTop: RAYA, paddingTop: 8 }}>
                      <div style={rotulo}>Hablar con el coach de esta verificación</div>
                      {cambiada && <div style={{ fontSize: 11, color: COLOR_DE.aviso, marginBottom: 4 }}>Has cambiado la escena desde esta verificación. Si quieres un veredicto del texto de ahora, verifícala otra vez.</div>}
                      {hilo.map((x, i) => (
                        <div key={i} style={{ fontSize: 12, lineHeight: 1.45, margin: "6px 0", padding: "6px 8px", borderRadius: 6, whiteSpace: "pre-wrap",
                          background: x.de === "autor" ? "var(--hi-papel-2)" : "transparent", border: x.de === "autor" ? "none" : RAYA }}>
                          <b style={{ color: x.de === "autor" ? CY : GD }}>{x.de === "autor" ? "Tú" : "Coach"}.</b> {x.texto}
                        </div>
                      ))}
                      {retirado.length > 0 && (
                        <div style={{ fontSize: 12, lineHeight: 1.45, color: SEMAFORO.verde, margin: "4px 0 6px" }}>
                          Queda apuntado: en esta escena ya no se te volverá a señalar {retirado.length === 1 ? "esto" : "nada de esto"}.
                          {retirado.map((x, i) => <div key={i} style={{ color: TENUE }}>· {x}</div>)}
                        </div>
                      )}
                      <textarea value={pregunta} onChange={(e) => setPregunta(e.target.value)} rows={3} disabled={hablando?.estado === "pidiendo"}
                        placeholder="Lo que no ves claro, o con lo que no estás de acuerdo…"
                        style={{ width: "100%", boxSizing: "border-box", marginTop: 4, padding: 6, fontSize: 12, fontFamily: "inherit", color: "var(--hi-tinta)", background: "var(--hi-papel)", border: RAYA, borderRadius: 6, resize: "vertical" }} />
                      <button onClick={preguntar} disabled={hablando?.estado === "pidiendo" || !pregunta.trim()} style={{ ...boton, marginTop: 4 }}>Preguntar al coach</button>
                      {hablando?.estado === "pidiendo" && <div style={{ fontSize: 12, color: COLOR_DE.aviso, marginTop: 6 }}>El coach está contestando…</div>}
                      {hablando?.estado === "error" && <div style={{ fontSize: 12, color: COLOR_DE.error, marginTop: 6 }}>{hablando.texto}</div>}
                      {hilo.length === 0 && hablando?.estado !== "pidiendo" && (
                        <div style={{ fontSize: 11, color: TENUE, marginTop: 4 }}>
                          Tiene delante tu escena, la ficha, las reglas del guion, la biblia y lo que ha dicho aquí arriba. Te contesta; no redacta por ti. Usa tu clave de OpenRouter.
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: TENUE, marginTop: 8, lineHeight: 1.4 }}>
                      {hecha.biblia
                        ? `Biblia: ${hecha.biblia.nombre}${hecha.biblia.recortada ? " (es muy larga: solo ha viajado el principio)" : ""}.`
                        : hecha.biblias_dudosas.length
                          ? `En esta carpeta hay biblias de varios libros (${hecha.biblias_dudosas.slice(0, 3).join(", ")}…) y no sé cuál es la de este: no se han comprobado datos contra ninguna. Llama a la tuya como al guion, o deja solo esa.`
                          : "No he encontrado la biblia en esta carpeta: no se han comprobado datos contra ella."}
                      {hecha.retiradas > 0 && ` Se ${hecha.retiradas === 1 ? "ha quitado 1 frase" : `han quitado ${hecha.retiradas} frases`} que la revisión proponía entre comillas: aquí no se proponen frases.`}
                      {" "}No se ha tocado tu documento.
                      {dolares(hecha.coste) ? ` Ha costado ${dolares(hecha.coste)}.` : ""}
                    </div>
                  </div>
                )}
              </div>

              <div style={{ margin: "10px 0 2px", borderTop: RAYA, paddingTop: 8 }}>
                {suCierre ? (
                  <div style={{ fontSize: 12, lineHeight: 1.45 }}>
                    <b style={{ color: SEMAFORO.verde }}>✓ Escena cerrada</b>{diaYHora(suCierre.cuando) ? ` el ${diaYHora(suCierre.cuando)}` : ""} · {miles(suCierre.palabras)} palabras
                    <div style={{ fontSize: 11, color: TENUE, overflowWrap: "anywhere" }}>Con el texto de «{suCierre.de}». Es el que irá a la novela.</div>
                    {suMedida && <div style={{ fontSize: 12, marginTop: 4, color: suMedida.registro === "sin_registro" || suMedida.registro === "parcial" ? COLOR_DE.aviso : "var(--hi-suave)" }}><Punto zona={suMedida.zona} />{fraseDeMedida(suMedida)}</div>}
                    {suMedida && fraseDelAviso(suMedida) && <div style={{ fontSize: 12, marginTop: 2, color: COLOR_DE.aviso }}>{fraseDelAviso(suMedida)}</div>}
                    {fraseDeCoincidencia(suCierre.coincide) && (
                      <div style={{ fontSize: 12, marginTop: 4, color: COLOR_DE.aviso }}>
                        ⚠ {fraseDeCoincidencia(suCierre.coincide)}
                        {suCierre.coincide!.tramos.map((t, i) => <div key={i} style={{ fontSize: 11, color: TENUE, overflowWrap: "anywhere" }}>«{t.cita}»</div>)}
                        {suCierre.coincide!.mas > 0 && <div style={{ fontSize: 11, color: TENUE }}>…y {suCierre.coincide!.mas} más.</div>}
                      </div>
                    )}
                    <button onClick={cerrar} disabled={cierre?.estado === "pidiendo"} style={{ ...enlace, marginRight: 12 }}>Cerrarla otra vez con el texto de ahora</button>
                    <button onClick={reabrir} style={enlace}>Reabrirla</button>
                    {avisoDeTandas}
                  </div>
                ) : (
                  <>
                    <button onClick={cerrar} disabled={cierre?.estado === "pidiendo"} style={boton}>Cerrar esta escena</button>
                    <div style={{ fontSize: 11, color: TENUE, marginTop: 4 }}>
                      Cuando la des por buena. Se queda con el texto que tiene ahora en el documento guardado (guarda antes), y con las cerradas se construye la novela.
                    </div>
                    {avisoDeTandas}
                  </>
                )}
                {cierre?.estado === "pidiendo" && <div style={{ fontSize: 12, color: COLOR_DE.aviso, marginTop: 4 }}>Cerrando la escena…</div>}
                {cierre?.estado === "igual" && <div style={{ fontSize: 11, color: TENUE, marginTop: 4 }}>El texto guardado es el mismo con el que ya estaba cerrada.</div>}
                {cierre?.estado === "error" && <div style={{ fontSize: 12, color: COLOR_DE.error, marginTop: 4 }}>{cierre.texto}</div>}
              </div>
              </>)}

              <div style={{ marginTop: 10 }}>
                {ficha.campos.map(([nombre, valor], i) => {
                  const v = nombre ? hecha?.campos.find((c) => c.campo === nombre) : undefined;
                  return (
                    <div key={i} style={{ marginBottom: 9 }}>
                      {nombre && <div style={{ fontSize: 11, fontWeight: 700, color: CY, textTransform: "uppercase", letterSpacing: 0.4 }}>{nombre}</div>}
                      <div style={{ fontSize: 13, lineHeight: 1.45, color: "var(--hi-suave)" }}>{valor}</div>
                      {v && !v.comprobaciones && (
                        <div style={{ fontSize: 12, lineHeight: 1.45, marginTop: 3, paddingLeft: 8, borderLeft: `2px solid ${COLOR_VEREDICTO[v.veredicto ?? "nada"]}` }}>
                          <b style={{ color: COLOR_VEREDICTO[v.veredicto ?? "nada"] }}>{DICE_VEREDICTO[v.veredicto ?? "nada"]}</b>{v.porque ? ` ${v.porque}` : ""}
                          {v.cita && <div style={cita}>«{v.cita}»</div>}
                          {v.sin_prueba && <div style={{ fontSize: 11, color: COLOR_DE.aviso }}>La cita que da no está en tu texto: tómalo con reserva.</div>}
                        </div>
                      )}
                      {v?.comprobaciones && (() => {
                        // Con lista: el veredicto es una cuenta. A la vista, lo que pide atención; el resto, al desplegar.
                        const color = v.opinion ? TENUE : COLOR_VEREDICTO[v.veredicto ?? "nada"];
                        const todas = v.comprobaciones, abierto = !!desplegados[nombre];
                        const aLaVista = abierto ? todas : pendientesDe(todas);
                        return (
                          <div style={{ fontSize: 12, lineHeight: 1.45, marginTop: 3, paddingLeft: 8, borderLeft: `2px solid ${color}` }}>
                            <b style={{ color }}>{v.opinion ? "Opinión del coach (no cuenta)." : DICE_VEREDICTO[v.veredicto ?? "nada"]}</b>{cuentaDeCampo(v) ? ` ${cuentaDeCampo(v)}` : ""}
                            {aLaVista.map((x) => {
                              const m = marcaDe(x.estado), esOpinion = x.estado === "opinion_si" || x.estado === "opinion_no";
                              return (
                                <div key={x.n} style={{ marginTop: 3, color: x.estado === "si" || x.estado === "acordado" ? TENUE : undefined }}>
                                  <span title={m.dice} style={{ color: esOpinion || x.estado === "acordado" ? TENUE : x.estado === "si" ? SEMAFORO.verde : x.estado === "no" ? SEMAFORO.rojo : SEMAFORO.ambar, fontWeight: 700 }}>{m.marca}</span>{" "}
                                  {x.pide}{x.estado !== "si" && x.estado !== "no" ? <span style={{ color: TENUE }}> ({m.dice})</span> : null}
                                  {x.porque && <div style={{ paddingLeft: 14 }}>{x.porque}</div>}
                                  {x.cita && <div style={{ ...cita, paddingLeft: 14 }}>«{x.cita}»</div>}
                                </div>
                              );
                            })}
                            {todas.length > pendientesDe(todas).length && (
                              <button onClick={() => setDesplegados((d) => ({ ...d, [nombre]: !abierto }))} style={{ ...enlace, display: "block", marginTop: 3, fontSize: 11 }}>
                                {abierto ? "Ver solo lo que pide atención" : todas.length === 1 ? "Ver la comprobación" : `Ver las ${todas.length} comprobaciones`}
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", borderTop: RAYA, paddingTop: 8, marginTop: 4 }}>
                <button onClick={() => local && ver(local.dir, local.ruta, ficha.anterior)} disabled={!ficha.anterior} style={enlace}>{ficha.anterior ? `‹ ${ficha.anterior}` : ""}</button>
                <button onClick={() => local && ver(local.dir, local.ruta, ficha.siguiente)} disabled={!ficha.siguiente} style={enlace}>{ficha.siguiente ? `${ficha.siguiente} ›` : ""}</button>
              </div>
            </div>
          ) : (
            <div style={caja}>Elige en la lista la escena que vas a escribir y aquí tendrás su ficha.</div>
          ))}

          {vista === "escritura"
            ? <button onClick={() => setVerIndice((v) => !v)} style={{ ...enlace, margin: "8px 0 6px" }}>Todas las escenas {verIndice ? "▲" : "▼"}</button>
            : <div style={{ ...rotulo, margin: "12px 0 4px" }}>Las escenas del guion <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, color: TENUE }}>· pulsa una para ir a escribirla</span></div>}
          {(verIndice || vista === "guion") && (
            <div style={{ maxHeight: vista === "guion" ? 420 : 300, overflow: "auto", border: RAYA, borderRadius: 8, padding: 8 }}>
              {indice.capitulos.map((c, i) => (
                <div key={i} style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700 }}>{c.titulo}</div>
                  {c.escenas.map((e) => (
                    <button key={e.numero} onClick={() => { if (!local) return; void ver(local.dir, local.ruta, e.numero); if (vista === "guion") alIr?.("escritura"); }}
                      style={{ ...enlace, display: "block", textAlign: "left", padding: "2px 0", color: e.numero === ficha?.numero ? GD : CY }}>
                      {novela?.cerradas[e.numero] ? <span style={{ color: SEMAFORO.verde }}>✓ </span> : null}{e.titulo}{e.palabras ? ` · ${miles(e.palabras)}` : ""}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}

          {vista === "guion" && indice.avisos.length > 0 && (
            <div style={{ fontSize: 11, color: "var(--hi-aviso)", marginTop: 8 }}>
              {indice.avisos.slice(0, 4).map((a, i) => <div key={i}>⚠ {a}</div>)}
            </div>
          )}

          {vista === "escritura" && novela && (
            <div style={{ ...caja, marginTop: 12 }}>
              <div style={rotulo}>Construir la novela</div>
              <div style={{ fontSize: 12 }}>
                <b>{novela.hechas}</b> de {novela.total} escenas cerradas{novela.hechas ? "" : ". Cierra las que des por buenas y aquí se montan en un solo Word."}
              </div>
              {novela.hechas > 0 && novela.medida?.total && <div style={{ fontSize: 12, marginTop: 4 }}><Punto zona={novela.medida.total.zona} />{fraseDeNovela(novela.medida.total)}</div>}
              {novela.hechas > 0 && fraseDelAviso(novela.medida?.total) && <div style={{ fontSize: 12, marginTop: 2, color: COLOR_DE.aviso }}>{fraseDelAviso(novela.medida?.total)}</div>}
              {novela.hechas > 0 && novela.medida?.total?.zona && <div style={{ fontSize: 11, color: TENUE, marginTop: 2 }}>{LEYENDA_DEL_SEMAFORO} {LETRA_PEQUENA_DE_LA_IA}</div>}
              {novela.hechas > 0 && (
                <>
                  <label style={{ display: "block", fontSize: 11, color: TENUE, marginTop: 8 }}>Título de la portada
                    <input type="text" value={tituloDeLaNovela} maxLength={200} onChange={(e) => setPortada((p) => ({ ...p, titulo: e.target.value }))} disabled={construida?.estado === "pidiendo"} style={campo} />
                  </label>
                  <label style={{ display: "block", fontSize: 11, color: TENUE, marginTop: 6 }}>Autor
                    <input type="text" value={autorDeLaNovela} maxLength={120} placeholder="Tu nombre o tu seudónimo" onChange={(e) => setPortada((p) => ({ ...p, autor: e.target.value }))} disabled={construida?.estado === "pidiendo"} style={campo} />
                  </label>
                  {!autorDeLaNovela.trim() && <div style={{ fontSize: 11, color: COLOR_DE.aviso, marginTop: 4 }}>Sin autor, la portada y los créditos saldrán sin nombre.</div>}
                  <button onClick={construir} disabled={construida?.estado === "pidiendo"} style={{ ...boton, marginTop: 8 }}>Construir la novela</button>
                  <div style={{ fontSize: 11, color: TENUE, marginTop: 4, lineHeight: 1.45 }}>
                    Un Word nuevo con portada, página de créditos, índice de capítulos y la última versión cerrada de cada escena, en el orden del guion.
                    Las escenas van sin su rótulo del guion, separadas por «* * *». No toca tu manuscrito.
                  </div>
                </>
              )}
              {construida?.estado === "pidiendo" && <div style={{ fontSize: 12, color: COLOR_DE.aviso, marginTop: 6 }}>Montando la novela…</div>}
              {construida?.estado === "error" && <div style={{ fontSize: 12, color: COLOR_DE.error, marginTop: 6 }}>{construida.texto}</div>}
              {construida?.estado === "hecha" && (
                <div style={{ fontSize: 12, color: COLOR_DE.bien, marginTop: 6, lineHeight: 1.45, wordBreak: "break-word" }}>
                  Creada «{construida.datos.nombre}», con portada, créditos, índice, {construida.datos.escenas} {construida.datos.escenas === 1 ? "escena" : "escenas"} y {miles(construida.datos.palabras)} palabras.
                  {construida.datos.abierto ? " Se está abriendo en Word." : ""} Está en la carpeta «{construida.datos.carpeta}», junto a este documento.
                  <div style={{ color: TENUE, marginTop: 4 }}>El índice lleva los capítulos, sin número de página.</div>
                  {construida.datos.faltan.length > 0 && (
                    <div style={{ color: TENUE, marginTop: 4 }}>
                      Sin cerrar todavía: {construida.datos.faltan.join(", ")}{construida.datos.faltan_mas ? ` y ${construida.datos.faltan_mas} más` : ""}.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {vista === "escritura" && local && (
            <Detector dir={local.dir} doc={local.ruta} deLaNovela refresco={`${novela?.hechas ?? 0}:${construida?.estado === "hecha" ? construida.datos.nombre : ""}`} puente={puente} />
          )}

          {vista === "escritura" && autoria && (
            <div style={{ ...caja, marginTop: 12 }}>
              <div style={rotulo}>Registro de autoría</div>
              {autoria.ilegible ? (
                <div style={{ fontSize: 12, color: COLOR_DE.error }}>Esta carpeta tiene un registro de autoría que no se puede leer. No se toca: lleva este libro a una carpeta suya.</div>
              ) : autoria.empezado && autoria.de_este_guion ? (
                <div style={{ fontSize: 12, lineHeight: 1.45 }}>
                  <span style={{ color: SEMAFORO.verde }}>✓ </span>Empezado el {diaYHora(autoria.entry_ts)}, {nombreDeEntrada(autoria.entry_point)}.
                  <div style={{ fontSize: 11, color: TENUE, marginTop: 2 }}>Lo que verificas, llevas a Word y cierras queda apuntado junto al libro. De aquí saldrá el certificado.</div>
                </div>
              ) : autoria.empezado ? (
                <div style={{ fontSize: 12, color: COLOR_DE.aviso, lineHeight: 1.45 }}>
                  Esta carpeta ya tiene un registro de autoría que no se empezó desde este guion («{autoria.titulo || "sin título"}», entrada: {autoria.entry_point}).
                  Lo que hagas aquí se apuntaría en él. Lleva este libro a una carpeta suya y empieza allí su registro.
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 12, lineHeight: 1.45 }}>Todavía no has empezado el registro de autoría de este libro. El certificado solo cuenta lo que pasa desde que lo empiezas.</div>
                  <label style={{ display: "block", fontSize: 11, color: TENUE, marginTop: 8 }}>¿Con qué llegas?
                    <select value={entrada} onChange={(e) => { setEntrada(e.target.value); setArranque(null); }} disabled={arranque?.estado === "pidiendo"} style={campo}>
                      {ENTRADAS.map(([id, texto]) => <option key={id} value={id}>{texto}</option>)}
                    </select>
                  </label>
                  {arranque?.estado === "confirmar" && (
                    <div style={{ fontSize: 12, color: COLOR_DE.aviso, marginTop: 6 }}>Se va a empezar {nombreDeEntrada(entrada)}. Esto no se puede cambiar después.</div>
                  )}
                  <button onClick={empezarRegistro} disabled={arranque?.estado === "pidiendo"} style={{ ...boton, marginTop: 8 }}>
                    {arranque?.estado === "confirmar" ? "Sí, empezar el registro" : "Empezar el registro de autoría"}
                  </button>
                  {arranque?.estado === "confirmar" && <button onClick={() => setArranque(null)} style={{ ...enlace, marginLeft: 10 }}>Cancelar</button>}
                </>
              )}
              {arranque?.estado === "error" && <div style={{ fontSize: 12, color: COLOR_DE.error, marginTop: 6 }}>{arranque.texto}</div>}
              {(autoria.libros_en_la_carpeta ?? 0) > 1 && (
                <div style={{ fontSize: 11, color: COLOR_DE.aviso, marginTop: 6, lineHeight: 1.45 }}>
                  En esta carpeta hay registros de {autoria.libros_en_la_carpeta} libros. El diario de autoría es de la carpeta y los mezcla: cada libro, en la suya.
                </div>
              )}
            </div>
          )}

          {vista === "guion" && (<>
          <div style={{ marginTop: 12 }}>
            <button onClick={() => setVerEmpezar((v) => !v)} style={{ ...enlace, color: TENUE, fontSize: 11 }}>Empezar el manuscrito desde el guion {verEmpezar ? "▲" : "▼"}</button>
            {verEmpezar && (
              <div style={{ ...caja, marginTop: 6 }}>
                <b>Un Word nuevo con la estructura de tu guion.</b> Lleva un título por capítulo, otro por escena y, debajo de cada uno, el hueco para escribirla. No trae nada escrito: el texto lo pones tú.
                <p style={nota}>
                  No es obligatorio. Puedes escribir en un documento tuyo y tener la ficha aquí al lado. Pero ayuda: verificar una escena necesita que lleve su título
                  («1.2 · …»), y este Word ya los trae puestos, en el orden del guion.
                </p>
                <p style={nota}>Solo si este libro todavía no tiene manuscrito.</p>
                <p style={{ ...nota, wordBreak: "break-all" }}>Se creará en: {local?.dir}</p>
                <button onClick={esqueleto} disabled={!!ocupado} style={boton}>Crear el manuscrito con la estructura del guion</button>
                {empezar && (
                  <div style={{ marginTop: 8, fontSize: 12, color: COLOR_DE[empezar.tono] }}>
                    {empezar.texto}
                    {empezar.abrir && <button onClick={() => abrir(empezar.abrir!)} style={{ ...enlace, display: "block", marginTop: 4 }}>Abrir «{empezar.abrir}» en Word</button>}
                  </div>
                )}
              </div>
            )}
          </div>

          <button onClick={() => setVerDetalles((v) => !v)} style={{ ...enlace, color: TENUE, fontSize: 11, marginTop: 8 }}>Detalles {verDetalles ? "▲" : "▼"}</button>
          {verDetalles && (
            <div style={{ fontSize: 11, color: TENUE, wordBreak: "break-all" }}>
              <div>Guion leído de: {indice.fuente}</div>
              <div>Carpeta: {local?.dir}</div>
              <div>Esta pestaña no sigue al cursor: eliges la escena en la lista.</div>
            </div>
          )}
          </>)}
        </>
      )}
    </div>
  );
}

const nota: React.CSSProperties = { fontSize: 12, color: TENUE, margin: "6px 0" };
const rotulo: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: GD, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 3 };
const cita: React.CSSProperties = { fontSize: 12, color: TENUE, fontStyle: "italic" };
const retiradoPorElCoach: React.CSSProperties = { color: TENUE, opacity: 0.75 };
const caja: React.CSSProperties = { border: RAYA, borderRadius: 10, padding: 12, marginBottom: 10, fontSize: 13 };
const enlace: React.CSSProperties = { background: "none", border: "none", color: CY, cursor: "pointer", fontSize: 12, padding: 0 };
const campo: React.CSSProperties = { display: "block", width: "100%", boxSizing: "border-box", marginTop: 2, padding: "6px 8px", fontSize: 12, fontFamily: "inherit", color: "var(--hi-tinta)", background: "var(--hi-papel)", border: RAYA, borderRadius: 6 };
/** El punto del semáforo de la IA. Sin zona (no se sabe) no pinta nada: un punto gris parecería un dato. */
function Punto({ zona }: { zona?: Zona | null }) {
  if (!zona) return null;
  return <span role="img" aria-label={NOMBRE_DE_ZONA[zona]} title={`${NOMBRE_DE_ZONA[zona]} · ${LEYENDA_DEL_SEMAFORO}`}
    style={{ display: "inline-block", width: 9, height: 9, borderRadius: 5, marginRight: 6, background: SEMAFORO[zona] }} />;
}

const boton: React.CSSProperties = { padding: "8px 12px", borderRadius: 8, border: "none", background: CY, color: "var(--hi-sobre-acento)", fontWeight: 700, fontSize: 12, cursor: "pointer" };

/** Un guion de la carpeta, con su botón para traerlo. El propuesto por el coach se dice. */
function EligeGuion({ nombre, rotulo, ocupado, alElegir }: { nombre: string; rotulo: string; ocupado: boolean; alElegir: () => void }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
      <span style={{ flex: 1, fontSize: 12, overflowWrap: "anywhere" }}>
        «{nombre}»{/guion_propuesto_\d+\.docx$/i.test(nombre) && <span style={{ color: TENUE }}> · propuesto por tu coach</span>}
      </span>
      <button onClick={alElegir} disabled={ocupado} style={{ ...boton, padding: "6px 10px", whiteSpace: "nowrap" }}>{rotulo}</button>
    </div>
  );
}
